/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, concatMap, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { IdentityService } from 'src/app/core/identity.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ContractRequestService } from 'src/app/shared/lib/contract-request/contract-request.service';
import { ContractRequestAdd, MarginDifference } from 'src/app/shared/lib/contract-request/models';
import { FetchArgs } from 'src/app/shared/models/core';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { ExistingContractRequestDialog } from '../../dialogs/existing-contract-request.dialog.component';
import { DuplicateContractRequestKey } from '../../enum';
import { MarginCalculatorContractInfoHelperService } from '../../services/margin-calculator-contract-info-helper.service';
import { MarginCalculatorFacilityService } from '../../services/margin-calculator-facility.service';
import { FORM_NAMES, MarginCalculatorFormService } from '../../services/margin-calculator-form.service';
import { MarginCalculatorMarginService } from '../../services/margin-calculator-margin.service';
import {
    DuplicateMarginActions,
    LinkToOfferActions,
    MarginCalculatorApiActions,
    MarginCalculatorPageActions,
    PipelineMarginActions,
    ReviewAndSendActions,
    UpdateMarginActions,
    ValidateFormActions
} from '../actions';
import * as Selectors from '../selectors';
import { WINDOW } from 'src/app/core/core.module';
import dayjs from 'dayjs';
import { PathfindersFlags } from 'src/app/shared/models/enums/team-feature-flags/pathfinders-feature-flags.enum';
import { MarginType } from '../../enum/margin-type.enum';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';

@Injectable()
export class UpdateMarginEffects {
    validateFormBeforeSave$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                UpdateMarginActions.saveMargin,
                ReviewAndSendActions.reviewAndSendMargin,
                DuplicateMarginActions.duplicateMargin,
                PipelineMarginActions.pipelineMargin,
                LinkToOfferActions.linkToOfferSaveMargin
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginAsModel),
                this.store.select(Selectors.selectReferralBonus),
                this.store.select(Selectors.selectPayTo),
                this.store.select(Selectors.selectEducationReimbursements),
                this.store.select(Selectors.selectCertificationOtherText),
                this.store.select(Selectors.selectPinkFacility)
            ]),
            switchMap(
                ([
                    action,
                    margin,
                    referralBonus,
                    payToReferral,
                    selectedCerts,
                    certificateOtherText,
                    isPinkFacility
                ]) => {
                    const errorMessage =
                        this._marginService.validateMarginBeforeSave(margin) ||
                        this._marginService.validateReferralBonus(referralBonus, payToReferral) ||
                        this._marginService.validateCertificates(selectedCerts, certificateOtherText) ||
                        this._marginService.validateHousingForm(margin);

                    if (!errorMessage && isPinkFacility) {
                        return of(
                            ValidateFormActions.validateFormBeforeSaveFail({
                                message: "Facility doesn't match an existing facility profile. Please try again."
                            })
                        );
                    }

                    if (errorMessage) {
                        return of(ValidateFormActions.validateFormBeforeSaveFail({ message: errorMessage }));
                    }

                    return of(
                        ValidateFormActions.validateFormBeforeSaveSuccess({ margin, actionTriggered: action.type })
                    );
                }
            )
        );
    });

    saveMarginAfterSuccessfulValidation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ValidateFormActions.validateFormBeforeSaveSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginAsModel),
                this.store.select(Selectors.selectFeatureFlags),
                this.store.select(Selectors.selectMarginMedical),
                this.store.select(Selectors.selectMarginDental)
            ]),
            switchMap(([action, margin, featureFlags, marginMedical, marginDental]) => {
                const insuranceMedicalDentalEffectiveDateEnabled =
                    featureFlags && featureFlags[PathfindersFlags.InsuranceEffectiveDateEnabled];
                const wfdSimplifiedSampleMarginEnabled = featureFlags?.[PathfindersFlags.WFDSimplifiedSampleMargin];
                if (
                    insuranceMedicalDentalEffectiveDateEnabled &&
                    action.actionTriggered === DuplicateMarginActions.duplicateMargin.type
                ) {
                    this._marginService.updateMarginWithDefaultEffectiveDates(margin, marginMedical, marginDental);
                }

                if (
                    wfdSimplifiedSampleMarginEnabled &&
                    action.actionTriggered === DuplicateMarginActions.duplicateMargin.type &&
                    margin.marginTypeId === MarginType.WorkforceDisruptionSample
                ) {
                    margin.marginTypeId = MarginType.Travel;
                }

                return this._marginService.postMargin(margin).pipe(
                    map((marginId) => UpdateMarginActions.saveMarginCommonSuccess({ marginId })),
                    catchError((response) => {
                        const disablePipelinedMarginUpdateIsActive =
                            featureFlags && featureFlags[PathfindersFlags.DisablePipelinedMarginUpdate];
                        return of(
                            UpdateMarginActions.saveMarginCommonFail({
                                message: disablePipelinedMarginUpdateIsActive
                                    ? MarginCalculatorContractInfoHelperService.getApiValidationError(
                                          response,
                                          'Failed to save margin.'
                                      )
                                    : 'Failed to save margin.'
                            })
                        );
                    })
                );
            })
        );
    });

    saveMarginCommonFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail, ValidateFormActions.validateFormBeforeSaveFail),
            switchMap(({ message }) => {
                return of(showToasterMessage({ message }));
            })
        );
    });

    saveMarginSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeSaveMargin)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(UpdateMarginActions.saveMarginSuccess(action)))
        );
    });

    saveMarginFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeSaveMargin)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(UpdateMarginActions.saveMarginFail(action)))
        );
    });

    reviewAndSendMarginPostSave$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeReviewAndSend)]),
            filter(([, actionType]) => actionType),
            switchMap(() => of(ReviewAndSendActions.postSaveMargin()))
        );
    });

    reviewAndSendMarginLoadContractStatusAndChangeType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.postSaveMargin),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectUserId)
            ]),
            switchMap(([, facilityId, marginId, userId]) => {
                return forkJoin({
                    contractStatusForFacility$:
                        this._marginCalculatorFacilityService.getContractStatusForFacility(facilityId),
                    changeType$: this._contractRequestService.getChangeType(userId, marginId)
                }).pipe(
                    switchMap(({ contractStatusForFacility$: contractStatusForFacility, changeType$: changeType }) => {
                        return of(
                            ReviewAndSendActions.loadContractStatusAndChangeTypeSuccess({
                                marginId,
                                contractStatusForFacility,
                                changeType
                            })
                        );
                    })
                );
            })
        );
    });

    reviewAndSendMarginLoadContractStatusAndChangeTypeSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.loadContractStatusAndChangeTypeSuccess),
            switchMap(({ contractStatusForFacility }) => {
                if (contractStatusForFacility.isFacilityContractInGoodStanding) {
                    return of(ReviewAndSendActions.facilityContractInGoodStanding());
                }

                return of(
                    ReviewAndSendActions.reviewAndSendMarginFail({
                        message: 'Facility must have a signed contract in order to proceed.'
                    })
                );
            })
        );
    });

    loadUpdatedValidationsSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.facilityContractInGoodStanding),
            concatLatestFrom(() => [this.store.select(Selectors.selectMoneyMovementValidations)]),
            switchMap(([, moneyMovement]) => {
                if (moneyMovement.length > 0) {
                    return of(
                        ReviewAndSendActions.reviewAndSendMarginBlockedByViolations({
                            message: 'There are some rules disallowing the workflow to complete'
                        })
                    );
                }

                return of(ReviewAndSendActions.createContractRequest());
            })
        );
    });

    reviewAndSendMarginFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeReviewAndSend)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(ReviewAndSendActions.reviewAndSendMarginFail(action)))
        );
    });

    disableReviewAndSendOnCreateContractRequest$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.createContractRequest),
            switchMap(() =>
                of(
                    MarginCalculatorPageActions.setMarginReviewAndSendForContractRequest({
                        reviewAndSendEnabled: false
                    })
                )
            )
        );
    });

    createContractRequestToaster$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.createContractRequest),
            switchMap(() =>
                of(
                    showToasterMessage({
                        message: 'Saving Contract Request, please be patient.  New Page will open when done'
                    })
                )
            )
        );
    });

    createContractRequest$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.createContractRequest),
            concatLatestFrom(() => [this.store.select(Selectors.selectContractRequestData)]),
            switchMap(
                ([, { marginId, marginPct, recruiterId, facilityId, candidateId, ayaPaidBonusCoCApprovalEnabled }]) =>
                    of({
                        marginId,
                        marginPct,
                        recruiterId,
                        facilityId,
                        candidateId,
                        ayaPaidBonusCoCApprovalEnabled
                    })
            ),
            switchMap(
                ({ marginId, marginPct, recruiterId, facilityId, candidateId, ayaPaidBonusCoCApprovalEnabled }) => {
                    return combineLatest([
                        this._contractRequestService.getDefaultContractRequestRules({ Id: marginId }),
                        this._contractRequestService.getMarginProperties(marginId),
                        this._contractRequestService.getChangeType(candidateId, marginId)
                    ]).pipe(
                        map(([rules, marginProperties, changeType]) => ({
                            rules,
                            marginId,
                            marginPct,
                            recruiterId,
                            facilityId,
                            candidateId,
                            marginProperties,
                            changeType,
                            ayaPaidBonusCoCApprovalEnabled
                        }))
                    );
                }
            ),
            switchMap(
                ({
                    marginId,
                    marginPct,
                    recruiterId,
                    facilityId,
                    rules,
                    marginProperties,
                    changeType,
                    ayaPaidBonusCoCApprovalEnabled
                }) => {
                    const rulesSource$ = changeType.previousMarginId
                        ? this._contractRequestService
                              .getMarginProperties(changeType.previousMarginId)
                              .pipe(
                                  map((props) =>
                                      MarginCalculatorContractInfoHelperService.ruleChecker(
                                          rules,
                                          props,
                                          marginProperties,
                                          ayaPaidBonusCoCApprovalEnabled
                                      )
                                  )
                              )
                        : of(rules);

                    return rulesSource$.pipe(
                        map((loadedRules) => ({
                            rules: loadedRules,
                            marginId,
                            marginPct,
                            recruiterId: +recruiterId,
                            facilityId
                        }))
                    );
                }
            ),
            switchMap((val) => of(ReviewAndSendActions.addContractRequest(val)))
        );
    });

    addContractRequest$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.addContractRequest),
            switchMap(({ rules, marginId, marginPct, recruiterId, facilityId }) => {
                const contractRequestAdd: ContractRequestAdd = {
                    marginId,
                    marginPercentage: marginPct,
                    statusId: 1,
                    specialRequest: null,
                    recruiterId,
                    facilityId,
                    rules,
                    changes: null
                };

                return this._contractRequestService.addContractRequest(contractRequestAdd).pipe(
                    map((contractRequestId) =>
                        ReviewAndSendActions.createContractRequestSuccess({ contractRequestId, marginId })
                    ),
                    catchError(() =>
                        of(
                            ReviewAndSendActions.createContractRequestFail({
                                message: 'Failed to create contract request'
                            })
                        )
                    )
                );
            })
        );
    });

    createContractRequestSuccessNewWindow$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ReviewAndSendActions.createContractRequestSuccess),
                tap(({ contractRequestId, marginId }) => {
                    this._window.open(
                        `//${this._window.location.host}/#/recruiting/contract-request/${contractRequestId}?marginId=${marginId}`,
                        `_blank`
                    );
                    this._window.close();
                })
            );
        },
        { dispatch: false }
    );

    createContractRequestFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeCreateContractRequest)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(ReviewAndSendActions.createContractRequestFail(action)))
        );
    });

    createContractRequestSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.createContractRequestSuccess),
            switchMap((action) =>
                of(ReviewAndSendActions.reviewAndSendMarginSuccess({ contractId: action.contractRequestId }))
            )
        );
    });

    contractMod$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.contractMod),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectClinician),
                this.store.select(Selectors.selectFacility),
                this.store.select(Selectors.selectContractStageDescription),
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate),
                this.store.select(Selectors.selectClinicianName),
                this.store.select(Selectors.selectRecruiter),
                this.store.select(Selectors.selectAccountManagerName),
                this.store.select(Selectors.selectAccountManagerEmail),
                this.store.select(Selectors.selectAccountCoordinatorEmail)
            ]),
            concatMap(
                ([
                    ,
                    marginId,
                    clinician,
                    facility,
                    contractStageDescription,
                    startDate,
                    endDate,
                    nurseName,
                    recruiter,
                    accountManagerName,
                    accountManagerEmail,
                    accountCoordinatorEmail
                ]) => {
                    if (!(clinician && facility)) {
                        return of(showToasterMessage({ message: 'Please select a nurse and facility!' }));
                    }
                    const contractModEmail$ = this._marginService.getContractModEmail(marginId);
                    const previousMarginId$ = this._marginService.getContractRequestPreviousMarginId(marginId);
                    return forkJoin({ contractModEmail$, previousMarginId$ }).pipe(
                        switchMap(({ contractModEmail$: contractModEmails, previousMarginId$: previousMarginId }) => {
                            if (contractModEmails?.[0]) {
                                const mailto = contractModEmails.join(';');
                                const cc = `${accountManagerEmail}; ${accountCoordinatorEmail ? accountCoordinatorEmail : ''}`;
                                const subject = `${nurseName} (${recruiter} and ${accountManagerName})`;
                                const body = `${dayjs().format('MM/DD/YYYY')}\r\n${contractStageDescription}\r\n${
                                    facility.hospitalName
                                }\r\n${dayjs(startDate).format('MM/DD/YYYY')}\t${dayjs(endDate).format('MM/DD/YYYY')}`;
                                return previousMarginId
                                    ? of(
                                          MarginCalculatorPageActions.contractModWithPreviousMarginId({
                                              mailto,
                                              cc,
                                              subject,
                                              body,
                                              previousMarginId
                                          })
                                      )
                                    : of(
                                          MarginCalculatorPageActions.contractModSendEmail({
                                              mailto,
                                              cc,
                                              subject,
                                              body
                                          })
                                      );
                            }
                            return of(MarginCalculatorPageActions.contractModNotFound());
                        })
                    );
                }
            )
        );
    });

    contractModWithMarginDifferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.contractModWithPreviousMarginId),
            concatLatestFrom(() => [this.store.select(Selectors.selectMarginId)]),
            switchMap(([{ mailto, cc, subject, body, previousMarginId }, marginId]) => {
                return this._contractRequestService.getMarginDifferences(previousMarginId, marginId).pipe(
                    switchMap((marginDifferences: MarginDifference[]) => {
                        const changeLogs = this._marginService.getContractModEmailChangeLogsSection(marginDifferences);
                        return of(
                            MarginCalculatorPageActions.contractModSendEmail({
                                mailto,
                                cc,
                                subject,
                                body: `${body}${changeLogs}`
                            })
                        );
                    })
                );
            })
        );
    });

    contractModSendEmail$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(MarginCalculatorPageActions.contractModSendEmail),
                tap(({ mailto, cc, subject, body }) => {
                    this._window.open(`mailto:${mailto}?cc=${cc}&subject=${subject})&body=${encodeURIComponent(body)}`);
                })
            );
        },
        { dispatch: false }
    );

    contractModNotFound$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.contractModNotFound),
            map(() =>
                showToasterMessage({
                    message: 'No emails were found for contract mod.',
                    duration: 5000
                })
            )
        );
    });

    duplicateMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.duplicateMargin),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsWFDSampleMargin),
                this.store.select(Selectors.selectJobId)
            ]),
            switchMap(([, isWFDSampleMargin, jobId]) => {
                const duplicateJobId = jobId && isWFDSampleMargin ? jobId : null;
                this._marginFormService?.parentForm?.markAsPristine();
                return of(DuplicateMarginActions.duplicateMargin({ jobId: duplicateJobId }));
            })
        );
    });

    duplicateMarginSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeDuplicate)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(DuplicateMarginActions.duplicateMarginSuccess(action)))
        );
    });

    duplicateMarginFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeDuplicate)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(DuplicateMarginActions.duplicateMarginFail(action)))
        );
    });

    pipelineMarginAfterSavedTrigger$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypePipeline)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(PipelineMarginActions.pipelineMarginAfterSave(action)))
        );
    });

    pipelineMarginAfterSave$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PipelineMarginActions.pipelineMarginAfterSave),
            switchMap(({ marginId }) => this._marginService.pipelineMargin(marginId)),
            switchMap((marginContract) => of(PipelineMarginActions.pipelineMarginSuccess({ marginContract }))),
            catchError(() => of(PipelineMarginActions.pipelineMarginFail({ message: 'Pipeline failed' })))
        );
    });

    pipelineMarginFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypePipeline)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(PipelineMarginActions.pipelineMarginFail(action)))
        );
    });

    linkToOfferSaveMarginSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeLinkToOffer)]),
            filter(([, actionType]) => actionType),
            switchMap(() => of(LinkToOfferActions.linkToOfferMarginSaveSuccess()))
        );
    });

    linkToOfferSaveMarginFail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveMarginCommonFail),
            concatLatestFrom(() => [this.store.select(Selectors.selectSaveMarginActionTypeLinkToOffer)]),
            filter(([, actionType]) => actionType),
            switchMap(([action]) => of(LinkToOfferActions.linkToOfferMarginSaveFail(action)))
        );
    });

    saveClicked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveClicked),
            concatLatestFrom(() => [this.store.select(Selectors.selectIsWFDSampleMargin)]),
            switchMap(([, isWFDSampleMargin]) => {
                if (isWFDSampleMargin) {
                    const allowedToLinkMargin = this._identityService?.hasPermission('Recruiting', 'Jobs-SampleMargin');
                    if (!allowedToLinkMargin) {
                        return of(
                            MarginCalculatorPageActions.noOp({
                                debug: 'saveClicked$ effect: not allowed to link margin'
                            })
                        );
                    }
                    return this._confirmationService
                        .showDialog({
                            title: 'Workforce Disruption Sample Margin',
                            message: 'This is a Workforce Disruption Job. are you sure you want to save changes?',
                            action: 'SAVE',
                            cancelText: 'CANCEL',
                            needCancel: true
                        })
                        .pipe(
                            switchMap((confirm) => {
                                if (confirm) {
                                    return of(UpdateMarginActions.saveConfirmed());
                                }
                                return of(UpdateMarginActions.cancelSaveMargin());
                            })
                        );
                }
                return of(UpdateMarginActions.saveConfirmed());
            })
        );
    });

    saveConfirmed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveConfirmed),
            switchMap(() => {
                const valid =
                    this._marginFormService.validateForm(FORM_NAMES.overview) &&
                    this._marginFormService.validateForm(FORM_NAMES.contract_shift);
                const formDisabled = this._marginFormService.parentForm.disabled;
                if (valid && !formDisabled) {
                    return of(UpdateMarginActions.saveMargin());
                }
                return of(UpdateMarginActions.saveConfirmedFailed());
            })
        );
    });

    saveConfirmedFailed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.saveConfirmedFailed),
            switchMap(() => {
                return of(showToasterMessage({ message: 'Please fill out all required fields' }));
            })
        );
    });

    reviewAndSendClicked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.reviewAndSendClicked),
            concatLatestFrom(() => [this.store.select(Selectors.selectIsWFDSampleMargin)]),
            switchMap(([, isWFDSampleMargin]) => {
                if (isWFDSampleMargin) {
                    return this._confirmationService
                        .showDialog({
                            title: 'Workforce Disruption Sample Margin',
                            message:
                                'This is a Workforce Disruption Sample Margin. It cannot be sent to the contracts team.',
                            action: 'CLOSE',
                            cancelText: null,
                            needCancel: false
                        })
                        .pipe(
                            switchMap(() => {
                                return of(MarginCalculatorPageActions.noOp({ debug: 'reviewAndSendClicked$ effect' }));
                            })
                        );
                }
                return of(ReviewAndSendActions.getContractRequestCount());
            })
        );
    });

    getContractRequestCount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReviewAndSendActions.getContractRequestCount),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectClinicianName),
                this.store.select(Selectors.selectFacilityName)
            ]),
            switchMap(([, candidateName, facility]) => {
                const args: FetchArgs = {
                    filter: {
                        candidateName,
                        facility,
                        status: ['open', 'drafting', 'pending', 'recalled', 'rejected']
                    },
                    skip: null,
                    pageSize: null
                };

                return this._contractRequestService.getContractRequestCount(args).pipe(
                    map((count) => MarginCalculatorApiActions.getContractRequestCountSuccess({ count })),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.getContractRequestCountFail({
                                message: 'Failed to get contract request count'
                            })
                        )
                    )
                );
            })
        );
    });

    getContractRequestCountSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.getContractRequestCountSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectClinicianName),
                this.store.select(Selectors.selectFacilityName),
                this.store.select(Selectors.selectMarginId)
            ]),
            switchMap(([{ count }, candidateName, facility, marginId]) => {
                if (count > 0) {
                    const dialog = this._dialog.open(ExistingContractRequestDialog, {
                        data: {
                            candidateName,
                            facility,
                            contractRequestCount: count,
                            marginId
                        },
                        panelClass: ['nova__dialog', 'nova__dialog--md'],
                        disableClose: true
                    });
                    return dialog.afterClosed().pipe(
                        switchMap((result) => {
                            if (result === DuplicateContractRequestKey.ReviewAndSend) {
                                return of(ReviewAndSendActions.reviewAndSendMargin());
                            }
                            return of(ReviewAndSendActions.existingContractRequestDialogCanceled());
                        })
                    );
                }

                return of(ReviewAndSendActions.reviewAndSendMargin());
            })
        );
    });

    linkSampleMarginToJob$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateMarginActions.linkSampleMarginToJob),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectJobId)
            ]),
            switchMap(([, marginId, jobId]) => {
                if (!marginId || !jobId) {
                    return of(
                        UpdateMarginActions.linkSampleMarginToJobFail({
                            message: 'Unable to link sample margin to job due to missing margin or job ID.'
                        })
                    );
                }

                return this._recruitingService.linkSampleMarginToJob(jobId, marginId).pipe(
                    map(() =>
                        UpdateMarginActions.linkSampleMarginToJobSuccess({
                            message: `Sample margin ${marginId} linked to job ${jobId}.`,
                            linkedToJob: true
                        })
                    ),
                    catchError(() =>
                        of(
                            UpdateMarginActions.linkSampleMarginToJobFail({
                                message: 'Unable to link sample margin to job.'
                            })
                        )
                    )
                );
            })
        );
    });

    broadcastWfdSampleMarginLinked$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(UpdateMarginActions.linkSampleMarginToJobSuccess),
                concatLatestFrom(() => [
                    this.store.select(Selectors.selectMarginId),
                    this.store.select(Selectors.selectJobId)
                ]),
                tap(([, marginId, jobId]) => {
                    const broadcastChannel = new BroadcastChannel(`wfdSampleMarginChannel_${jobId}`);
                    broadcastChannel.postMessage({ sampleMarginId: marginId });
                    broadcastChannel.close();
                })
            );
        },
        { dispatch: false }
    );

    verifySampleMarginLinkedToJob$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectJobId),
                this.store.select(Selectors.selectMarginTypeId)
            ]),
            filter(
                ([, marginId, jobId, marginTypeId]) =>
                    marginTypeId === MarginType.WorkforceDisruptionSample && !!marginId && !!jobId
            ),
            switchMap(([, marginId, jobId]) =>
                this._recruitingService.verifySampleMarginLinkedToJob(jobId, marginId).pipe(
                    map((linkedToJob) => MarginCalculatorPageActions.setMarginLinkedToJob({ linkedToJob })),
                    catchError(() => of(MarginCalculatorPageActions.setMarginLinkedToJob({ linkedToJob: false })))
                )
            )
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly _marginService: MarginCalculatorMarginService,
        private readonly _marginFormService: MarginCalculatorFormService,
        private readonly _contractRequestService: ContractRequestService,
        private readonly _marginCalculatorFacilityService: MarginCalculatorFacilityService,
        private readonly _identityService: IdentityService,
        private readonly _confirmationService: ConfirmDialogService,
        private readonly _dialog: MatDialog,
        private readonly _recruitingService: RecruitingService,
        @Inject(WINDOW) private readonly _window: Window
    ) {}
}
